import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getWorkspacesByGroup, getWorkspacesByGroups } from '../services/workspaceService';
import type { Workspace, WorkspaceContextType } from '../types';
import { useAuth } from './AuthContext';
import { useGroups } from './GroupContext';

// 案件コンテキスト作成
const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

// 案件プロバイダーコンポーネント
export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { groups } = useGroups();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<{ groupId: string | null }>({ groupId: null });

  // 案件一覧を取得
  const refreshWorkspaces = useCallback(async () => {
    if (!user || groups.length === 0) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      let fetchedWorkspaces: Workspace[];

      if (filter.groupId) {
        // 特定グループでフィルタ
        fetchedWorkspaces = await getWorkspacesByGroup(filter.groupId);
      } else {
        // 全グループの案件を取得
        const groupIds = groups.map((g) => g.id);
        fetchedWorkspaces = await getWorkspacesByGroups(groupIds);
      }

      setWorkspaces(fetchedWorkspaces);

      // 現在選択中の案件が一覧にない場合はリセット
      if (currentWorkspace && !fetchedWorkspaces.find((w) => w.id === currentWorkspace.id)) {
        setCurrentWorkspace(null);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, [user, groups, filter.groupId, currentWorkspace]);

  // グループ一覧が変わったら案件を再取得
  useEffect(() => {
    if (groups.length > 0) {
      refreshWorkspaces();
    } else {
      setWorkspaces([]);
      setLoading(false);
    }
  }, [groups, refreshWorkspaces]);

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    loading,
    filter,
    setFilter,
    setCurrentWorkspace,
    refreshWorkspaces,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

// カスタムフック
export const useWorkspaces = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaces must be used within a WorkspaceProvider');
  }
  return context;
};

export default WorkspaceContext;
